import { PermissionsDirectiveModule } from '@b2b/common/directives/permissions';
import { AsyncFilterModule, parseUrl, ParseUrlFilterModule } from '@b2b/common/filters';
import { ACMETenantResponse, TenantData, ThemesData } from '@b2b/common/resolvers';
import { safeApply } from '@b2b/common/rxjs';
import { ModalService, ModalServiceModule } from '@b2b/common/services';
import { ApiService } from '@b2b/common/services/api/types';
import { CalendarService } from '@b2b/common/services/calendar';
import { FeatureService, FeatureServiceModule } from '@b2b/common/services/feature/feature.service';
import { arrayConcat } from '@b2b/common/services/helpers';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { UserPreferencesService } from '@b2b/common/services/userPreferences/types';
import { ensureArray } from '@b2b/common/utils/coerce-array';
import { ACMEResponse, TODO } from '@b2b/types/core';
import { Order } from '@b2b/types/order';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IQService, IScope, ITimeoutService, IWindowService } from 'angular';
import { OrdersDetailServiceFactory, OrdersDetailServiceModule } from '../services/orders-detail.service';
import { partitionAddonsAndFeesLast } from '../../utils';

OrdersDetailController.$inject = [
  '$scope',
  '$state',
  '$filter',
  '$timeout',
  '$q',
  '$window',
  'ApiService',
  'CalendarService',
  'OrderService',
  'PermissionsService',
  'HelpersService',
  'OrderData',
  'ThemesData',
  'UserPreferencesService',
  'ThemesHelperService',
  'FeatureService',
  'OrdersDetailServiceFactory',
  'TenantData',
  'ModalService',
];

function OrdersDetailController(
  $scope: IScope & { ordersDetail: TODO },
  $state: StateService,
  $filter: IFilterService,
  $timeout: ITimeoutService,
  $q: IQService,
  $window: IWindowService,
  ApiService: ApiService,
  CalendarService: CalendarService,
  OrderService: TODO,
  PermissionsService: PermissionsService,
  HelpersService: TODO,
  OrderData: { data: ACMEResponse<Order> },
  ThemesData: { data: ACMEResponse<ThemesData> },
  UserPreferencesService: UserPreferencesService,
  ThemesHelperService: ThemesHelperService,
  FeatureService: FeatureService,
  OrdersDetailServiceFactory: OrdersDetailServiceFactory,
  TenantData: { data: ACMETenantResponse<TenantData> },
  modalService: ModalService,
) {
  const ordersDetailService = OrdersDetailServiceFactory.createInstance({
    onOnlinePaymentLinkChanged: (params) => {
      self.onlinePaymentLinkCopied = false;
      self.onlinePaymentLink = self.generateOnlinePaymentLink(params.onlinePaymentUUID);
      $scope.$apply();
    },
  });

  const ctrl = this;

  ctrl.vm = ordersDetailService.getSnapshot();

  ordersDetailService.vm$
    .pipe(
      safeApply($scope, {
        next: (vm) => {
          ctrl.vm = vm;
        },
      }),
    )
    .subscribe();

  const self = {
    title: 'Order Details',

    model: {} as Order,
    notes: '',
    dateFormat: UserPreferencesService.getDateFormat('standardDate', 'dateFormat'),
    hourFormat: UserPreferencesService.getDateFormat('standardTime', 'hourFormat'),
    dateTimeFormat: UserPreferencesService.getDateTimeFormat('sortDateTime'),
    isSaving: false,
    isEditingVoucher: false,
    hasMembership: false,
    checkNumber: '',
    voucherNumber: '',
    totalRetail: 0,
    totalTicketQuantity: 0,
    totalAddons: 0,
    totalMemberships: 0,
    totalPaid: 0,
    isTaxExempt: false,
    showMembershipQuantity: true,
    billingSameAsOrderContact: false,

    tenantData: {} as TenantData,
    onlinePaymentLink: '',
    onlinePaymentLinkCopied: false,

    paymentMethods: {
      0: 'CreditCard',
      1: 'Voucher',
      2: 'Check',
      3: 'Other',
    } as const,

    secondaryContacts: [] as TODO[],

    membershipIds: [] as string[],

    getItemType: function (type) {
      return OrderService.getItemType(type, true);
    },

    orderDetails: {
      columns: 1,
      data: 'ordersDetail.model',
      include: [
        'orderNumber',
        'formattedCreatedOn',
        'legacy',
        'secondaryContactFirstName',
        'secondaryContactLastName',
        'secondaryContactEmail',
        'checkInStatus',
        'orderRequestLink',
      ],
      mappings: {
        formattedCreatedOn: 'Order Date',
        legacy: 'Order Type',
        secondaryContactFirstName: 'Contact First Name',
        secondaryContactLastName: 'Contact Last Name',
        secondaryContactEmail: 'Contact Email',
        orderRequestLink: 'Order Request ID',
      },
    },

    orderNotes: {
      columns: 1,
      data: 'ordersDetail.model',
      include: ['notes'],
      mappings: {
        notes: 'Order Notes',
      },
    },

    paymentDetails: {
      columns: 1,
      data: 'ordersDetail.model',
      include: [
        'saleChannel',
        'paymentMethodName',
        'ccLastFourDigits',
        'creditCardBrand',
        'paymentId',
        'refundStatus',
        'orderBalance',
        'invoiceAfterDesc',
      ],
      mappings: {
        paymentId: '',
        paymentMethodName: 'Payment Method',
        ccLastFourDigits: 'Card Last 4 Digits',
        invoiceAfterDesc: 'Invoice After Event',
      },
      filters: {
        orderBalance: function (value) {
          return $filter('currency')(value);
        },
      },
    },

    customerDetails: {
      columns: 1,
      hideLabels: true,
      data: 'ordersDetail.model',
      include: ['customerLink', 'customerEmail', 'customerPhone'],
      mappings: {},
    },

    billingDetails: {
      columns: 1,
      hideLabels: true,
      data: 'ordersDetail.model',
      include: ['billingFullName', 'billingEmail', 'billingPhoneNumber'],
      mappings: {
        billingFullName: 'Name',
        billingEmail: 'Email',
        billingPhoneNumber: 'Phone Number',
      },
    },

    orderContactDetails: {
      columns: 1,
      hideLabels: true,
      data: 'ordersDetail.model',
      include: ['contactFullName', 'emailHref', 'phoneNumber'],
      mappings: {
        contactFullName: 'Name',
        emailHref: 'Email',
      },
    },

    membershipsDetails: {
      columns: 1,
      hideLabels: true,
      include: ['membershipName', 'membershipPrimaryCardholder', 'membershipExpirationDate'],
    },

    accountDetails: {
      columns: 1,
      data: 'ordersDetail.model',
      include: ['accountLink', 'companyName'],
      mappings: {
        accountLink: 'Organization Name',
      },
    },

    orderLanguages: {
      columns: 1,
      data: 'ordersDetail.model',
      include: ['preferredLanguage'],
    },

    init: function () {
      $scope.$on('$destroy', () => {
        ordersDetailService.destroy();
      });

      $scope.$emit('Application::contentTitleChange', {
        title: self.title,
      });

      if (OrderData && OrderData.data.success) {
        self.success.init(OrderData);
      } else {
        self.error.init(OrderData);
      }

      if (ThemesData && ThemesData.data.success) {
        ThemesHelperService.setModel(ThemesData.data.payload);
        self.showMembershipQuantity = ThemesHelperService.getCompany('membershipSales') as TODO;
      }

      if (TenantData && TenantData.data.success) {
        self.tenantData = TenantData.data.tenant;
      }

      $scope.$on('ResendEmail::Send', self.onResendEmailSend);
      $scope.$on('UserPreferencesService:onDateFormatLoad', self.onDateFormatLoad);
    },

    initHeaderButtons: function () {
      const contentTitle = {
        title: self.title,
        buttonLeft: {
          label: 'Back',
          action: function () {
            window.history.go(-1);
          },
        },
        buttonRight: {
          label: 'Edit Order',
          permissions: ['ORDER_UPDATE'],
          action: function () {
            $state.go('application.orders.edit', { orderId: self.model.orderId });
          },
          isDisabled: self.isDisabled(),
        },
      };

      $scope.$emit('Application::contentTitleChange', contentTitle);
    },

    hasName: function (order) {
      return order.contactFirstName || order.contactLastName;
    },

    getAccountContacts: function (id) {
      ApiService.partners.contacts
        .list(id, HelpersService.getPaginator())
        .then((response) => self.success.getAccountContacts(response))
        .catch(() => self.error.getAccountContacts());
    },

    findContact: function (id) {
      let result;

      for (let i = 0, ii = self.secondaryContacts.length; i < ii; i++) {
        if (self.secondaryContacts[i].id === id) {
          result = self.secondaryContacts[i];
          break;
        }
      }
      return result;
    },

    isMembership: function (event) {
      return event.eventId.toLowerCase() === 'membership';
    },

    isMembershipConversion: function (ticket) {
      return ticket && ticket.conversionStatus && ticket.conversionStatus === 'MembershipConversion';
    },

    getOrderMembershipId: function (membership) {
      let result = '';

      if (membership.itemDetailId) {
        result = membership.itemDetailId;
      }

      return result;
    },

    getOrderMembershipLevelName: function (membershipItem) {
      let result = '';

      if (membershipItem.membershipLevelName) {
        result = membershipItem.membershipLevelName + ': ' + (membershipItem.itemTypeName || '');
      } else {
        result = membershipItem.itemTypeName || '';
      }

      return result;
    },

    getOrderBalance: function () {
      return HelpersService.currencyFloatToInteger(self.model.orderBalance || 0);
    },

    processTicketQuantity: function () {
      const order = self.model;

      for (let i = 0, ii = order.eventItems.length; i < ii; i++) {
        if (order.eventItems[i].items instanceof Array) {
          for (let j = 0, jj = order.eventItems[i].items.length; j < jj; j++) {
            self.determineTicketType(order.eventItems[i].items[j]);
          }
        }
      }
    },

    determineTicketType: function (item) {
      let type;

      switch (item.itemType) {
        case 'Inventory':
        case 'Event':
          if (item.itemType === 'Inventory') {
            type = 'totalAddons';
          } else if (item.itemType === 'Event') {
            type = 'totalTicketQuantity';
          }
          if (item && item.tickets instanceof Array) {
            for (let i = 0, ii = item.tickets.length; i < ii; i++) {
              if (item.tickets[i].ticketStatus.toLowerCase() === 'normal') {
                self[type]++;
              }
            }
          }
          break;
        case 'MembershipPurchase':
        case 'MembershipRenewal':
          self.totalMemberships++;
          break;
      }
    },

    getRetail: function () {
      const order = self.model;
      let result = 0;

      for (const eventItems of order.eventItems) {
        for (const ticket of ensureArray(eventItems.items)) {
          const price = self.isDiscounted(ticket)
            ? HelpersService.currencyFloatToInteger(ticket.discountedSubtotal)
            : HelpersService.currencyFloatToInteger(ticket.subtotal);

          result = result + price;
        }
      }

      self.totalRetail = HelpersService.integerToCurrencyFloat(result);

      return self.totalRetail;
    },

    allowCancel: function () {
      return self.model.refundStatus !== 'Fully Refunded' && !self.hasMembership;
    },

    isRefunded: function () {
      return self.model.refundStatus === 'Fully Refunded' || self.isChargedBack();
    },

    isChargedBack: function () {
      return self.model.refundStatus === 'Charged Back';
    },

    isDisabled: function () {
      return self.isChargedBack();
    },

    isDiscounted: function (ticket) {
      const subtotal = HelpersService.currencyFloatToInteger(ticket.subtotal);
      const discountedSubtotal = HelpersService.currencyFloatToInteger(ticket.discountedSubtotal);

      return subtotal !== discountedSubtotal;
    },

    getPaid: function () {
      let result = 0;
      const order = self.model;
      const balance = self.getOrderBalance();

      for (let i = 0, ii = order.eventItems.length; i < ii; i++) {
        if (order.eventItems[i].items instanceof Array) {
          for (let j = 0, jj = order.eventItems[i].items.length; j < jj; j++) {
            result += HelpersService.currencyFloatToInteger(order.eventItems[i].items[j].discountedSubtotal || 0);
          }
        }
      }

      result = result - balance;

      /**
       * Multiplying by -1 to display a negative number
       */
      self.totalPaid = HelpersService.integerToCurrencyFloat(result) * -1;

      return result;
    },

    getCouponCode: function (ticket) {
      let result = '';
      if (ticket && ticket.couponDetail && ticket.couponDetail.code) {
        result = ticket.couponDetail.code;
      }
      return result;
    },

    hasPurchasedItems: function (items) {
      let result = 0;

      if (items instanceof Array) {
        for (let i = 0, ii = items.length; i < ii; i++) {
          result += items[i].quantity || 0;
        }
      }

      return result > 0;
    },

    checkContacts: function (order: Order) {
      if (!order) {
        return false;
      }

      const billingContactInfo =
        (order.billingFirstName || '') +
        (order.billingLastName || '') +
        (order.billingEmail || '') +
        (order.billingPhoneNumber || '');

      const orderContactInfo =
        (order.contactFirstName || '') +
        (order.contactLastName || '') +
        (order.email || '') +
        (order.phoneNumber || '');

      return billingContactInfo === orderContactInfo;
    },

    processOrderLinks: function (order) {
      if (order.accountName && order.accountId) {
        order.accountLink = parseUrl(
          $state.href('application.partners.detail', { id: order.accountId }),
          order.accountName,
        );
      } else {
        order.accountLink = '';
      }

      if (order.orderRequestId) {
        order.orderRequestLink = parseUrl(
          $state.href('application.orders.requests.edit', { id: order.orderRequestId }),
          order.orderRequestId,
        );
      } else {
        order.orderRequestLink = '';
      }

      return order;
    },

    sortOrderEvents: function (a, b) {
      const eventId = a.eventId.toLowerCase();
      if (eventId !== 'addons' && eventId !== 'fees') {
        return -1;
      }

      return 0;
    },

    process: function (order: Order) {
      let hasMembership = false;
      const hasMembershipConversion = false;

      order = self.processOrderLinks(order);

      order.memberships = [];

      if (order.membershipId && order.membershipId !== 'null') {
        self.membershipIds.push(order.membershipId);
      }

      self.billingSameAsOrderContact = self.checkContacts(order);

      order.billingFullName = arrayConcat([order.billingFirstName, order.billingLastName], ' ');
      order.contactFullName = arrayConcat([order.contactFirstName, order.contactLastName], ' ');
      order.emailHref = order.email;

      if (FeatureService.isEnabled(FeatureService.customers) && order.customerId) {
        const href = $state.href('application.customers.individuals.detail', { id: order.customerId });

        ApiService.customers
          .detail({ customerId: order.customerId })
          .then((response) => response.data.payload)
          .then((customer) => {
            const customerFullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ');

            order.customerLink = PermissionsService.check('CUSTOMERS_VIEW')
              ? parseUrl(href, customerFullName)
              : customerFullName;
            order.customerEmail = customer.email;
            order.customerPhone = customer.phoneNumber ?? '';
          });
      }

      order.invoiceAfterDesc = order.invoiceAfter ? 'Yes' : 'No';

      order.eventItems = partitionAddonsAndFeesLast(ensureArray(order.eventItems));

      // Merge fee items into the Addons event so they display together
      const feesEventIndex = order.eventItems.findIndex((e) => e.eventId === 'Fees');
      const addonsEventIndex = order.eventItems.findIndex((e) => e.eventId === 'Addons');
      if (feesEventIndex !== -1 && addonsEventIndex !== -1) {
        const feesEvent = order.eventItems[feesEventIndex];
        if (feesEvent.items instanceof Array) {
          order.eventItems[addonsEventIndex].items = ensureArray(order.eventItems[addonsEventIndex].items).concat(
            feesEvent.items,
          );
        }
        order.eventItems.splice(feesEventIndex, 1);
      }

      for (let i = 0, ii = order.eventItems.length; i < ii; i++) {
        if (order.eventItems[i].eventDate) {
          order.eventItems[i].startDate = CalendarService.toFormat(
            CalendarService.parseDate(order.eventItems[i].eventDate),
            UserPreferencesService.getDateFormat('shortDate', 'dateFormat'),
          );
          order.eventItems[i].startHour = CalendarService.toFormat(
            CalendarService.parseDate(order.eventItems[i].eventDate),
            UserPreferencesService.getDateFormat('standardTime', 'hourFormat'),
          );
        } else {
          order.eventItems[i].startDate = 'Date N/A';
          order.eventItems[i].startHour = 'Time N/A';
        }

        if (order.eventItems[i].state === 'cancelled') {
          order.eventItems[i].isCancelled = true;
          order.eventItems[i].formattedCancellationDate = CalendarService.toFormat(
            order.eventItems[i].cancellationDate,
            HelpersService.dateFormats.standardDateTime,
          );
        } else {
          order.eventItems[i].isCancelled = false;
          order.eventItems[i].formattedCancellationDate = '';
        }

        // TODO: b2bAddEventToOrder - remove this? trying to remove extra dynamic fields
        if (order.eventItems[i].state === 'suspended') {
          order.eventItems[i].isSuspended = true;
        }

        if (!hasMembership) {
          hasMembership = self.isMembership(order.eventItems[i]);
        }

        if (!order.membershipId && self.isMembership(order.eventItems[i])) {
          self.setMembershipIds(order.eventItems[i]);
        }

        // This is an event, so check if it has atleast 1 converted ticket
        if (!hasMembership && !hasMembershipConversion && order.eventItems[i].items instanceof Array) {
          for (let j = 0, jj = order.eventItems[i].items.length; j < jj && !hasMembershipConversion; j++) {
            if (
              order.eventItems[i].items[j] &&
              order.eventItems[i].items[j].tickets instanceof Array &&
              order.eventItems[i].items[j].tickets.length
            ) {
              order.eventItems[i].hasMembershipConversion = self.isMembershipConversion(order.eventItems[i].items[j]);
              if (order.eventItems[i].hasMembershipConversion) {
                break;
              }
            }
          }
        }
      }

      self.hasMembership = hasMembership;

      order.emailLanguages = {
        code: 'en-us',
        name: 'English (United States)',
      };

      order.preferredLanguage = order.emailLanguages.name;

      order.formattedCreatedOn = CalendarService.toFormat(
        CalendarService.parseDate(order.createdOn),
        self.dateTimeFormat,
      );

      return order;
    },

    setMembershipIds: function (order) {
      order.items.forEach(function (item) {
        self.membershipIds.push(item.itemDetailId);
      });
    },

    hasMembershipCancelItem: function (model) {
      return HelpersService.path(model, ['eventItems'], []).some((eventItem) => {
        return HelpersService.path(eventItem, ['items'], []).some((item) => {
          return item.itemType === 'MembershipCancel';
        });
      });
    },

    getMembershipData: function () {
      const uniqueIds = Array.from(new Set(self.membershipIds));
      const membershipPromises = uniqueIds.map((item) => ApiService.membership.detail(item));

      return $q.all(membershipPromises).then((responses: any[]) => {
        const memberships = responses.map((response) => {
          const membership = response.data.payload;

          if (membership.auxiliaryMembership) {
            return;
          }

          membership.primaryCardholder = membership.cardholders.find(function (cardholder) {
            return cardholder.cardType === 'primary';
          });

          const membershipPrimaryCardholder = arrayConcat(
            [
              'Primary:',
              membership.primaryCardholder.prefix,
              membership.primaryCardholder.firstName,
              membership.primaryCardholder.lastName,
            ],
            ' ',
          );

          const membershipName = parseUrl(
            $state.href('application.membership.detail', { id: membership.id }),
            membership.membershipCategory + ': ' + membership.offeringName,
          );

          const membershipExpirationDate =
            'Expires: ' +
            CalendarService.toFormat(
              membership.membershipExpirationDate,
              UserPreferencesService.getDateFormat('shortDate', 'dateFormat'),
            );

          self.model.memberships.push({
            membershipName: membershipName,
            membershipPrimaryCardholder: membershipPrimaryCardholder,
            membershipExpirationDate: membershipExpirationDate,
          });

          return membership;
        });

        return memberships;
      });
    },

    update: function () {
      self.model.notes = self.notes + '';
      self.isSaving = true;
      ApiService.orders
        .notes({
          id: $state.params.orderId,
          notes: self.model.notes,
        })
        .then((response) => self.success.update(response))
        .catch((response) => self.error.update(response));
    },

    updateNotes: function () {
      //need this, but it can be empty
      self.update();
    },

    cancel: function () {
      window.history.go(-1);
    },

    updateVoucher: function () {
      self.isSaving = true;
      ApiService.orders
        .voucher({
          id: $state.params.orderId,
          paymentId: self.voucherNumber,
        })
        .then((response) => self.success.updateVoucher(response))
        .catch((response) => self.error.updateVoucher(response));
    },

    sendEmail: function (email: string, preferredLanguage: string) {
      ApiService.orders
        .email({
          id: self.model.orderId,
          email: email,
          preferredLanguage: preferredLanguage,
        })
        .then((response) => self.success.sendEmail(response))
        .catch((response) => self.error.sendEmail(response));
    },

    printOrderSummary: function () {
      const height = $(window).height();
      const width = $(window).width();

      const wHeight = height ? height - 20 : 0;
      const wWidth = width ? width - 20 : 0;

      console.log('ProgramsOrdersDetail::printOrderSummary', wHeight, wWidth);
      // Open print window
      HelpersService.setCookie('printorder', {
        orderId: self.model.orderId,
        isInvoice: false,
      });

      $timeout(function () {
        window.open('/app/orders/print', self.model.orderNumber, 'height=' + wHeight + ',width=' + wWidth + ',left=10');
      }, 10);
    },

    printInvoice: function () {
      const height = $(window).height();
      const width = $(window).width();

      const wHeight = height ? height - 20 : 0;
      const wWidth = width ? width - 20 : 0;

      console.log('ProgramsOrdersDetail::printInvoice', wHeight, wWidth);
      // Open print window
      HelpersService.setCookie('printorder', {
        orderId: self.model.orderId,
        isInvoice: true,
      });

      $timeout(function () {
        window.open(
          '/app/orders/print/invoice',
          self.model.orderNumber,
          'height=' + wHeight + ',width=' + wWidth + ',left=10',
        );
      }, 10);
    },

    isState: function (state) {
      return $state.current?.name?.indexOf('application.orders.detail.' + state) === 0;
    },

    getState: function (name) {
      const stateName = 'application.orders.detail.' + name;
      $state.go(stateName, { id: $state.params.id });
    },

    getPaymentMethod: function () {
      if (/check/i.test(self.model.paymentMethodName)) {
        self.paymentDetails.mappings.paymentId = 'Check Number';
        self.checkNumber = self.model.paymentId;
      }

      if (/voucher/i.test(self.model.paymentMethodName)) {
        self.paymentDetails.mappings.paymentId = 'Voucher Number';
        self.voucherNumber = self.model.paymentId;
      }

      if (/other/i.test(self.model.paymentMethodName)) {
        self.paymentDetails.mappings.paymentId = 'Payment ID';
      }

      if (/storedvalue/i.test(self.model.paymentMethodName)) {
        self.paymentDetails.include = self.paymentDetails.include.filter((k) => k !== 'paymentId');
      }
    },

    fetchOnlinePaymentLink: function () {
      ordersDetailService.fetchOnlinePaymentLink(self.model);
    },

    createOnlinePaymentLink: function () {
      ordersDetailService.createOnlinePaymentLink(self.model);
    },

    deleteOnlinePaymentLink: function () {
      modalService
        .confirm({
          title: 'Are you sure?',
          okText: 'Delete',
          message: 'Do you want to delete this link?',
        })
        .then((confirmed) => {
          if (!confirmed) {
            return;
          }

          return ordersDetailService.deleteOnlinePayment(ctrl.vm.onlinePaymentUUID);
        });
    },

    generateOnlinePaymentLink: function (onlinePaymentUUID: string): string {
      return ordersDetailService.generateOnlinePaymentLink({
        brandedUrl: ThemesHelperService.getCompany('brandedUrl') || '',
        tenantId: ThemesHelperService.getCompany('tenantId'),
        consumerPage: self.tenantData.consumerPage,
        onlinePaymentUUID,
      });
    },

    copyToClipboardOnlinePaymentLink: async function () {
      self.onlinePaymentLinkCopied = false;

      try {
        await $window.navigator.clipboard
          .writeText(self.generateOnlinePaymentLink(ctrl.vm.onlinePaymentUUID))
          .then(() => {
            self.onlinePaymentLinkCopied = true;
            $scope.$apply();
          });
      } catch (error) {
        console.error(error);
      }
    },

    success: {
      init: function (response) {
        // Check if account order or normal order for permissions
        if (response.data.payload.accountId) {
          if (!PermissionsService.check('ACCT_ORDERS_VIEW')) {
            console.log('ACCT_ORDERS_VIEW');
            $state.go('home.default', {}, { notify: true });
          }
          if (response.data.payload.accountContactId) {
            self.getAccountContacts(response.data.payload.accountId);
          }
        } else {
          if (!PermissionsService.check('ORDER_VIEW')) {
            console.log('ORDER_VIEW');
            $state.go('home.default', {}, { notify: true });
          }
        }
        self.model = self.process(response.data.payload);
        self.isTaxExempt = !!response.data.payload.isTaxExempt;

        if (self.membershipIds.length) {
          self.getMembershipData();
        }

        ordersDetailService.checkCancelMembershipEligility(self.model);

        self.fetchOnlinePaymentLink();

        self.getPaymentMethod();

        self.notes = self.model.notes ? self.model.notes + '' : '';
        self.processTicketQuantity();
        self.initHeaderButtons();
      },
      getAccountContacts: function (response) {
        self.secondaryContacts = response.data.payload.list;
        const orderSecondaryContact = self.findContact(self.model.accountContactId);
        if (orderSecondaryContact) {
          self.model.secondaryContactFirstName = orderSecondaryContact.firstName;
          self.model.secondaryContactLastName = orderSecondaryContact.lastName;
          self.model.secondaryContactEmail = orderSecondaryContact.email;
        }
      },
      update: function (response) {
        self.isSaving = false;
      },

      updateVoucher: function (response) {
        self.isSaving = false;
        self.isEditingVoucher = false;
        self.model.paymentId = self.voucherNumber;
      },
      sendEmail: function (response) {
        $state.go($state.current, { orderId: $state.params.orderId }, { reload: true });
      },
      printOrderSummary: function (response) {
        //pop up print window here
      },
    },

    error: {
      init: function (response) {
        self.isSaving = false;
        self.initHeaderButtons();
      },
      getAccountContacts: function () {},

      update: function (response) {
        self.isSaving = false;
      },

      updateVoucher: function (response) {
        self.isSaving = false;
        self.isEditingVoucher = false;
      },
      sendEmail: function (response) {},
      printOrderSummary: function (response) {},
    },

    onResendEmailSend: function (e, data) {
      self.sendEmail(data.email, data.preferredLanguage);
    },

    onDateFormatLoad: function (e, data) {
      self.dateFormat = UserPreferencesService.getDateFormat('standardDate', 'dateFormat');
      self.hourFormat = UserPreferencesService.getHourFormat();
      self.dateTimeFormat = UserPreferencesService.getDateTimeFormat('shortDateTime');
    },
  };

  $scope.ordersDetail = self;
  self.init();
}

export const OrderDetailsControllerModule = angular
  .module('acme.view.application.orders.detail.controllers', [
    uiRouter,
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.service.order',
    'acme.common.service.calendar',
    'acme.common.service.permissions',
    'acme.common.service.helpers',
    'acme.common.service.userPreferences',
    'acme.common.service.themesHelper',
    FeatureServiceModule,
    AsyncFilterModule,
    ParseUrlFilterModule,
    OrdersDetailServiceModule,
    PermissionsDirectiveModule,
    ModalServiceModule,
  ])

  .controller('OrdersDetailController', OrdersDetailController).name;
